import { Trash2 } from 'lucide-react';
import { formatMoney, formatQty, lineTotal, UNITS, type CurrencyCode } from '@tengesa/shared';
import type { ProductDto } from '../../lib/types';
import { useSession } from '../../app/session';
import { Select, TextInput } from '../../ui';
import { MoneyInput, ProductPicker, QtyInput } from '../../ui/inputs';

export interface DocLine {
  key: string;
  product: ProductDto;
  qtyMilli: number | null;
  unitCostCents: number | null;
  /** For adjustments: add to or remove from stock. */
  direction?: 'in' | 'out';
  purchaseOrderLineId?: string | null;
  batchNo?: string;
  expiryDate?: string;
}

let n = 0;
export function newLine(product: ProductDto, partial: Partial<DocLine> = {}): DocLine {
  return {
    key: `d${n++}`,
    product,
    qtyMilli: null,
    unitCostCents: product.costCents ?? null,
    ...partial,
  };
}

/** Table of product lines used by orders, deliveries, adjustments and transfers. */
export function LinesEditor({
  lines,
  onChange,
  branchId,
  showCost,
  showDirection,
  showExpiry,
  allowAdd = true,
  qtyLabel = 'Quantity',
}: {
  lines: DocLine[];
  onChange: (l: DocLine[]) => void;
  branchId?: string | null;
  showCost?: boolean;
  showDirection?: boolean;
  showExpiry?: boolean;
  allowAdd?: boolean;
  qtyLabel?: string;
}) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const update = (key: string, patch: Partial<DocLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const total = lines.reduce((a, l) => a + (l.qtyMilli && l.unitCostCents != null ? lineTotal(l.unitCostCents, l.qtyMilli) : 0), 0);
  return (
    <div>
      {allowAdd && (
        <div className="mb-3">
          <ProductPicker
            branchId={branchId}
            stockedOnly
            placeholder="Add a product: search by name, code or barcode"
            onPick={(p) => {
              if (lines.some((l) => l.product.id === p.id) && !showExpiry) return;
              onChange([...lines, newLine(p, showDirection ? { direction: 'out' } : {})]);
            }}
          />
        </div>
      )}
      {lines.length === 0 ? (
        <p className="rounded-[8px] border border-dashed border-line-strong px-4 py-6 text-center text-muted">No products added yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr className="border-b border-line text-sm text-muted">
                <th className="min-w-48 py-2 pr-3 font-medium">Product</th>
                {showDirection && <th className="w-36 py-2 pr-3 font-medium">Change</th>}
                <th className="w-32 py-2 pr-3 font-medium">{qtyLabel}</th>
                {showCost && <th className="w-36 py-2 pr-3 font-medium">Unit cost</th>}
                {showExpiry && <th className="w-60 py-2 pr-3 font-medium">Batch / expiry</th>}
                {showCost && <th className="w-24 py-2 pr-3 text-right font-medium">Line</th>}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {lines.map((l) => (
                <tr key={l.key} className="align-top">
                  <td className="py-2 pr-3">
                    <span className="font-medium">{l.product.name}</span>
                    <span className="block text-xs text-muted">
                      {l.product.sku}
                      {l.product.stock && ` — ${formatQty(l.product.stock.qtyMilli)} ${UNITS[l.product.unit as keyof typeof UNITS]?.short ?? ''} on hand`}
                    </span>
                  </td>
                  {showDirection && (
                    <td className="py-2 pr-3">
                      <Select aria-label="Add or remove" value={l.direction ?? 'out'} onChange={(e) => update(l.key, { direction: e.target.value as 'in' | 'out' })}>
                        <option value="out">Remove</option>
                        <option value="in">Add</option>
                      </Select>
                    </td>
                  )}
                  <td className="py-2 pr-3">
                    <QtyInput unit={l.product.unit} value={l.qtyMilli} onChange={(q) => update(l.key, { qtyMilli: q })} />
                  </td>
                  {showCost && (
                    <td className="py-2 pr-3">
                      {!showDirection || l.direction === 'in' ? (
                        <MoneyInput currency={cur} value={l.unitCostCents} onChange={(c) => update(l.key, { unitCostCents: c })} />
                      ) : (
                        <span className="block pt-2.5 text-sm text-muted">At average cost</span>
                      )}
                    </td>
                  )}
                  {showExpiry && (
                    <td className="py-2 pr-3">
                      {l.product.tracksExpiry ? (
                        <div className="grid grid-cols-2 gap-2">
                          <TextInput aria-label="Batch number" placeholder="Batch" value={l.batchNo ?? ''} onChange={(e) => update(l.key, { batchNo: e.target.value })} />
                          <TextInput aria-label="Expiry date" type="date" value={l.expiryDate ?? ''} onChange={(e) => update(l.key, { expiryDate: e.target.value })} />
                        </div>
                      ) : (
                        <span className="block pt-2.5 text-sm text-faint">Not tracked</span>
                      )}
                    </td>
                  )}
                  {showCost && (
                    <td className="py-2 pr-3 pt-4 text-right text-sm num">
                      {l.qtyMilli && l.unitCostCents != null && (!showDirection || l.direction === 'in') ? formatMoney(lineTotal(l.unitCostCents, l.qtyMilli), cur) : ''}
                    </td>
                  )}
                  <td className="py-2">
                    <button aria-label={`Remove ${l.product.name}`} onClick={() => onChange(lines.filter((x) => x.key !== l.key))} className="mt-1.5 rounded p-1.5 text-muted hover:bg-paper hover:text-danger">
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {showCost && !showDirection && (
              <tfoot>
                <tr className="border-t border-line">
                  <td colSpan={showExpiry ? 4 : 3} className="py-2 pr-3 text-right font-medium">Total cost</td>
                  <td className="py-2 pr-3 text-right font-semibold num">{formatMoney(total, cur)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

export function linesReady(lines: DocLine[], needCost = false) {
  return lines.length > 0 && lines.every((l) => l.qtyMilli && l.qtyMilli > 0 && (!needCost || l.unitCostCents != null));
}
